// src/components/CustomerFlow/Steps/PaymentProcessingStep.js

import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faLock, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import api from '../../../services/api';
import { trackMobilePaymentCardSaved } from '../../../analytics/events';
import './PaymentProcessingStep.css';

const PaymentProcessingStep = ({ 
  flowData, 
  stepStatuses,
  onNext
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCardSaved, setIsCardSaved] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);

  const paymentWidgetRef = useRef(null);

  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;

  // Extraer IDs del flowData (que viene de MobileCustomerFlowPage)
  const customerId = flowData?.tokenCustomerId;
  const requestId = flowData?.tokenRequestId;

  // Auto-activar el paso si está en pending (solo una vez)
  useEffect(() => {
    const currentStepStatus = stepStatuses['PaymentStep'];
    
    // Si el estado de PaymentStep es pending:
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      // Marcar como auto-activado para evitar re-ejecución
      setHasAutoActivated(true);
      
      // Llamar a la API para marcar el step como in_progress
      onNext({
        step_name: 'PaymentStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  const validateCardData = (cardData) => {
    if (!cardData?.source) {
      setErrorMessage('Datos de tarjeta incompletos. Por favor, inténtalo de nuevo.');
      return false;
    }
    return true;
  };

  const sendCardToApi = async (payload) => {
    setIsSubmitting(true);
    try {
      const response = await api.post(`/customers/${customerId}/cards`, payload);
      setIsSubmitting(false);

      if (response.status === 201 || response.status === 200) {
        setIsCardSaved(true);
        setErrorMessage('');
        
        // Guardar datos de la tarjeta en localStorage para mostrar en el paso final (con prefijo temp_)
        localStorage.setItem('temp_card_last_four', payload.last_four);
        localStorage.setItem('temp_card_brand_name', payload.brand.name);
        
        // Track payment card saved
        const branchId = localStorage.getItem('temp_tokenBranchId');
        trackMobilePaymentCardSaved({
          commerceId: branchId || 'mobile_flow', // Fallback
          branchId: branchId || undefined,
          flowId: flowData?.flow_id,
          cardBrand: payload.brand?.name
        });
        
        // Después de validar que la tarjeta se cargó correctamente:
        // Llamar nuevamente a la API para marcar el paso como completed
        const stepData = {
          step_name: 'PaymentStep',
          step_status: 'completed',
          payment_setup_completed: true,
          card_tokenized: true,
          setup_timestamp: new Date().toISOString()
        };
        
        // Actualizar el estado local del paso a completed
        // Pasar al siguiente paso
        setTimeout(() => {
          onNext(stepData, 'completed');
        }, 3000);
        
        return true;
      } else {
        console.error('Unexpected response status:', response.status);
        setErrorMessage('Ocurrió un problema al guardar la tarjeta. Intente nuevamente.');
        return false;
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error storing card token:', error);
      setErrorMessage('No se pudo guardar la tarjeta. Intente nuevamente.');
      return false;
      }
  };

  const saveCardToken = async (cardData) => {
    if (!validateCardData(cardData)) return;

    const { source } = cardData;
    const payload = {
      request_id: requestId,
      token: cardData.token,
      holder: {
        id: source.holder?.identification,
        name: source.holder?.name,
      },
      expiration: source.expiration,
      last_four: source.last_4,
      type: source.type,
      variant: source.variant,
      brand: {
        name: source.brand?.name,
        logo: source.brand?.logo_url
      },
      bank: {
        color: source.bank?.color || '',
        logo: source.bank?.logo_url || '',
        name: source.bank?.name || ''
      }
    };

    await sendCardToApi(payload);
  };

  const reloadWidget = () => {
    setErrorMessage('');
    setIsCardSaved(false);
    setIsWidgetLoading(true);
    setIsSubmitting(false);
    setReloadTrigger((prev) => prev + 1);
  };

  // Load the Cirenio script and show the widget
  useEffect(() => {
    // Verificar que tengamos los IDs necesarios
    if (!customerId || !requestId) {
      console.error('Missing required IDs:', { customerId, requestId });
      setErrorMessage('Error: Faltan datos del cliente o de la solicitud.');
      return;
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;

    script.onload = () => {
      if (window.CirenioPayments) {
        const payments = new window.CirenioPayments(
          publicKey,
          customerId, // Usar customerId directamente
          {
            containerId: 'cirenioPaymentContainer',
            externalId: `pr-${customerId}-${requestId}` // Usar IDs correctos
          }
        );

        payments.on('widget_start', () => setIsWidgetLoading(false));

        payments.on('tokenization_success', async (cardData) => {
          await saveCardToken(cardData);
        });

        payments.on('tokenization_error', () => {
          setErrorMessage('Hubo un problema al procesar tu tarjeta. Por favor, intenta nuevamente.');
        });

        payments.openWidget();
      }
    };

    script.onerror = () => {
      setErrorMessage('No se pudo cargar el widget de pagos. Revisa tu conexión o vuelve a intentarlo.');
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [scriptSrc, publicKey, customerId, requestId, reloadTrigger]);

  const renderErrorAlert = () => {
    if (!errorMessage) return null;

    if (errorMessage.includes('No se pudo cargar el widget de pagos')) {
      return (
        <div className="alert alert-warning text-center mt-3" role="alert">
          <FontAwesomeIcon icon={faCreditCard} className="me-2" />
          No se pudo cargar el widget de pagos. Revisa tu conexión o{' '}
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ textDecoration: 'underline' }}
            onClick={reloadWidget}
          >
            vuelve a intentarlo
          </button>.
        </div>
      );
    }

    return (
      <div className="alert alert-warning text-center mt-3" role="alert">
        <FontAwesomeIcon icon={faCreditCard} className="me-2" />
        {errorMessage}
      </div>
    );
  };

  const InfoModal = () => {
    if (!showInfoModal) return null;

    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="modal-content bg-white p-4 rounded-3" style={{
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <div className="mb-4">
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              Requisitos de la Tarjeta
            </h6>
            <ul className="list-unstyled">
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe ser una tarjeta de débito
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe estar a tu nombre
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe estar vinculada a la cuenta donde recibís tus ingresos
              </li>
            </ul>
          </div>
          
          <div className="mb-4">
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              ¿Para qué se utilizará?
            </h6>
            <p className="text-muted mb-2">
              Se utilizará como medio alternativo de cobranza y quedará vinculada para futuras solicitudes
            </p>
          </div>
          
          <div>
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faLock} className="me-2" />
              Seguridad
            </h6>
            <p className="text-muted mb-0">
              Tus datos son tratados con los más altos estándares de seguridad bancaria y están protegidos por la ley de protección de datos personales
            </p>
          </div>
          
          <div className="text-center mt-4">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowInfoModal(false)}
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="payment-processing-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
        <h3>Método de Pago</h3>
      </div>

      <div className="step-body">
        <div className="text-center mb-4">
          <p className="lead mb-1">
            Configura tu método de pago para los débitos automáticos
          </p>
          <p className='lead'>Usá una tarjeta de débito a tu nombre, vinculada a la cuenta donde recibís tus ingresos.</p>

          <div className="text-center">
            <button
              type="button"
              className="btn btn-link text-primary p-0 text-decoration-none"
              onClick={() => setShowInfoModal(true)}
            >
              <i className="bi bi-info-circle me-1"></i>
              ¿Para qué necesitamos tu tarjeta?
            </button>
          </div>
        </div>

        {/* Contenedor del widget optimizado para responsividad */}
        <div className="payment-widget-container">
          <div 
            className="payment-widget-wrapper" 
            style={{ 
              display: isCardSaved ? 'none' : 'block',
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}
          >
            {isWidgetLoading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="text-muted">Cargando formulario de pago...</p>
                <Skeleton height={680} />
              </div>
            )}
            <div
              id="cirenioPaymentContainer"
              ref={paymentWidgetRef}
              className="payment-widget"
              style={{ 
                display: isWidgetLoading ? 'none' : 'block',
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%'
              }}
            />
          </div>
        </div>

        {renderErrorAlert()}
      </div>

      {/* Mensaje de estado */}
      {isCardSaved && (
        <div className="text-center mt-4">
          <FontAwesomeIcon icon={faCreditCard} size="3x" className="text-success mb-3" />
          <h3 className="text-success">¡Tarjeta Configurada!</h3>
          <p className="lead">
            Tu tarjeta ha sido configurada exitosamente. 
            Estamos procesando la información...
          </p>
          <div className="spinner-border text-primary mt-3" role="status">
            <span className="visually-hidden">Procesando...</span>
          </div>
        </div>
      )}

      <InfoModal />
    </div>
  );
};

export default PaymentProcessingStep;
